import React, { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { GEMINI_MODELS } from '../config/gemini-models';
import {
  initializeAIService,
  getAIService,
  getFeedbackManager,
  completeWithFeedback,
  updateFeedback,
  type AICompletionRequest,
  type AICompletionResponse,
  type FeedbackType,
  type FeedbackCategory,
  type FeedbackStats,
} from '../lib/ai-service';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import toast from 'react-hot-toast';

// ============================================================================
// TYPES
// ============================================================================

interface AIContextType {
  // Service state
  isInitialized: boolean;
  isEnabled: boolean;
  monthlyBudget: number;
  currentMonthSpending: number;
  budgetRemaining: number;
  budgetExceeded: boolean;

  // AI Methods
  complete: (
    request: AICompletionRequest,
    feature: string,
    category: FeedbackCategory
  ) => Promise<{ response: AICompletionResponse; feedbackId: string | null }>;

  // Feedback methods
  submitFeedback: (
    feedbackId: string,
    feedbackType: FeedbackType,
    options?: {
      rating?: number;
      comment?: string;
      correctedVersion?: string;
    }
  ) => void;
  getFeedbackStats: (startDate?: Date, endDate?: Date) => FeedbackStats;

  // Budget management
  refreshBudgetStatus: () => void;
  updateMonthlyBudget: (newBudget: number) => void;

  // Feature toggles
  isFeatureEnabled: (feature: string) => boolean;
  toggleFeature: (feature: string, enabled: boolean) => void;

  // Usage stats
  getUsageStats: (startDate: Date, endDate: Date) => any;
}

interface AISettings {
  enabled: boolean;
  monthlyBudget: number;
  enabledFeatures: Record<string, boolean>;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const AIContext = createContext<AIContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface AIProviderProps {
  children: ReactNode;
}

export function AIProvider({ children }: AIProviderProps) {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  const [isInitialized, setIsInitialized] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [monthlyBudget, setMonthlyBudget] = useState(100); // $100/month default
  const [currentMonthSpending, setCurrentMonthSpending] = useState(0);
  const hasLoadedRef = useRef(false);
  const [enabledFeatures, setEnabledFeatures] = useState<Record<string, boolean>>({
    scope_of_work: true,
    email_optimizer: true,
    autocomplete: true,
    line_item_suggestions: true,
    pricing_intelligence: true,
    quote_completeness: true,
  });

  /**
   * Initialize the AI service with Gemini
   *
   * NOTE: The Gemini API key is stored securely in Firebase Cloud Functions.
   * We don't need the API key on the client side - all AI calls are proxied
   * through the callGemini Cloud Function.
   */
  const initializeAI = useCallback(async () => {
    try {
      // Initialize service without API key - calls are proxied through Cloud Functions
      // The apiKey field is kept for interface compatibility but not used for Gemini
      initializeAIService({
        provider: 'gemini',
        apiKey: 'proxied-via-cloud-function', // Placeholder - actual key is server-side
        model: GEMINI_MODELS.LITE, // Gemini 3 Flash Preview
        maxTokens: 8192,
        temperature: 0.7,
        rateLimitPerMinute: 30, // Client-side rate limit (server also enforces)
      });

      setIsInitialized(true);
      console.log('AI Service initialized successfully with Gemini 3 Flash Preview (via Cloud Function)');
    } catch (error) {
      console.error('Failed to initialize AI service:', error);
      setIsEnabled(false);
      toast.error('AI features could not be initialized');
    }
  }, []);

  /**
   * Load AI settings from localStorage
   */
  const loadSettings = useCallback(() => {
    try {
      const key = `ai_settings_${currentOrganization?.organizationId || 'default'}`;
      const stored = localStorage.getItem(key);

      if (stored) {
        const settings: AISettings = JSON.parse(stored);
        setIsEnabled(settings.enabled);
        setMonthlyBudget(settings.monthlyBudget);
        setEnabledFeatures(settings.enabledFeatures);
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
    }
    // Mark load complete so the save effect doesn't fire during initial hydration
    hasLoadedRef.current = true;
  }, [currentOrganization?.organizationId]);

  /**
   * Save AI settings to localStorage
   */
  const saveSettings = useCallback(() => {
    try {
      const key = `ai_settings_${currentOrganization?.organizationId || 'default'}`;
      const settings: AISettings = {
        enabled: isEnabled,
        monthlyBudget,
        enabledFeatures,
      };
      localStorage.setItem(key, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving AI settings:', error);
    }
  }, [currentOrganization?.organizationId, isEnabled, monthlyBudget, enabledFeatures]);

  // Initialize AI service on mount
  useEffect(() => {
    initializeAI();
  }, [initializeAI]);

  // Load settings from localStorage
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Calculate monthly spending
  useEffect(() => {
    if (isInitialized) {
      refreshBudgetStatus();
    }
  }, [isInitialized]);

  // Save settings whenever they change (skip during initial load)
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    saveSettings();
  }, [saveSettings]);

  /**
   * Refresh budget status for current month
   */
  const refreshBudgetStatus = () => {
    try {
      const aiService = getAIService();
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const stats = aiService.getUsageStats(startOfMonth, endOfMonth);
      setCurrentMonthSpending(stats.totalCost);
    } catch (error) {
      console.error('Error refreshing budget status:', error);
    }
  };

  /**
   * Update monthly budget
   */
  const updateMonthlyBudget = (newBudget: number) => {
    setMonthlyBudget(newBudget);
  };

  /**
   * Check if budget exceeded
   */
  const budgetExceeded = currentMonthSpending >= monthlyBudget;
  const budgetRemaining = Math.max(0, monthlyBudget - currentMonthSpending);

  /**
   * Make an AI completion request with feedback tracking
   */
  const complete = async (
    request: AICompletionRequest,
    feature: string,
    category: FeedbackCategory
  ): Promise<{ response: AICompletionResponse; feedbackId: string | null }> => {
    // Check if enabled
    if (!isEnabled || !isInitialized) {
      throw new Error('AI features are not enabled');
    }

    // Check budget
    if (budgetExceeded) {
      throw new Error(
        `Monthly AI budget of $${monthlyBudget} has been exceeded. Please increase your budget or wait until next month.`
      );
    }

    // Check if feature is enabled
    if (!isFeatureEnabled(feature)) {
      throw new Error(`AI feature '${feature}' is not enabled`);
    }

    try {
      const result = await completeWithFeedback(request, feature, category, {
        userId: user?.userId,
        organizationId: currentOrganization?.organizationId,
      });

      // Refresh budget after request
      refreshBudgetStatus();

      // Check if we're approaching budget limit (90%)
      const newSpending = currentMonthSpending + (result.response.usage.totalTokens * 0.0001);
      if (newSpending >= monthlyBudget * 0.9 && currentMonthSpending < monthlyBudget * 0.9) {
        toast.error(
          `Warning: AI budget is 90% consumed ($${newSpending.toFixed(2)} of $${monthlyBudget})`,
          { duration: 5000 }
        );
      }

      return result;
    } catch (error: any) {
      console.error('AI completion error:', error);
      toast.error(error.message || 'AI request failed');
      throw error;
    }
  };

  /**
   * Submit feedback on AI response
   */
  const submitFeedback = (
    feedbackId: string,
    feedbackType: FeedbackType,
    options?: {
      rating?: number;
      comment?: string;
      correctedVersion?: string;
    }
  ) => {
    try {
      updateFeedback(feedbackId, {
        feedbackType,
        rating: options?.rating,
        userComment: options?.comment,
        correctedVersion: options?.correctedVersion,
      });

      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  /**
   * Get feedback statistics
   */
  const getFeedbackStats = (startDate?: Date, endDate?: Date): FeedbackStats => {
    const feedbackManager = getFeedbackManager();
    return feedbackManager.getStats(startDate, endDate);
  };

  /**
   * Check if a feature is enabled
   */
  const isFeatureEnabled = (feature: string): boolean => {
    return enabledFeatures[feature] !== false;
  };

  /**
   * Toggle a feature on/off
   */
  const toggleFeature = (feature: string, enabled: boolean) => {
    setEnabledFeatures((prev) => ({
      ...prev,
      [feature]: enabled,
    }));
  };

  /**
   * Get usage statistics
   */
  const getUsageStats = (startDate: Date, endDate: Date) => {
    try {
      const aiService = getAIService();
      return aiService.getUsageStats(startDate, endDate);
    } catch (error) {
      console.error('Error getting usage stats:', error);
      return null;
    }
  };

  const value: AIContextType = {
    isInitialized,
    isEnabled,
    monthlyBudget,
    currentMonthSpending,
    budgetRemaining,
    budgetExceeded,
    complete,
    submitFeedback,
    getFeedbackStats,
    refreshBudgetStatus,
    updateMonthlyBudget,
    isFeatureEnabled,
    toggleFeature,
    getUsageStats,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAI(): AIContextType {
  const context = useContext(AIContext);
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
}

// ============================================================================
// CONVENIENCE HOOKS FOR SPECIFIC FEATURES
// ============================================================================

/**
 * Hook for generating scope of work
 */
export function useScopeOfWorkGenerator() {
  const { complete, isEnabled, budgetExceeded } = useAI();

  const generateScopeOfWork = async (context: {
    customerName: string;
    projectType?: string;
    lineItems: Array<{ description: string; category?: string }>;
    notes?: string;
  }): Promise<string> => {
    // Analyze line items to determine project complexity
    const hasInstallation = context.lineItems.some(item =>
      item.description.toLowerCase().includes('install') ||
      item.category?.toLowerCase().includes('installation')
    );
    const hasService = context.lineItems.some(item =>
      item.description.toLowerCase().includes('service') ||
      item.description.toLowerCase().includes('maintenance') ||
      item.category?.toLowerCase().includes('service')
    );
    const hasRepair = context.lineItems.some(item =>
      item.description.toLowerCase().includes('repair') ||
      item.description.toLowerCase().includes('fix')
    );

    const prompt = `Generate a professional scope of work document for the following generator service/installation project:

CUSTOMER: ${context.customerName}
PROJECT TYPE: ${context.projectType || 'General Service'}
PROJECT CHARACTERISTICS: ${hasInstallation ? 'Installation, ' : ''}${hasService ? 'Service/Maintenance, ' : ''}${hasRepair ? 'Repair' : ''}

LINE ITEMS (${context.lineItems.length} total):
${context.lineItems.map((item, i) => `${i + 1}. ${item.description}${item.category ? ` [${item.category}]` : ''}`).join('\n')}

ADDITIONAL CONTEXT: ${context.notes || 'None provided'}

COMPANY PROPOSAL BEST PRACTICES (CRITICAL - FOLLOW EXACTLY):

MATERIALS SPECIFICITY:
- Be specific enough so customer knows exactly what's included
- Include quantities and key component details
- Group shoppable items to maintain information advantage (don't itemize every bolt and screw)
- Include proprietary details like: "includes necessary fittings, gaskets, and adapters for installation"
- Avoid vague lump sums like "Misc. Materials" - always specify what's covered
- Balance: detailed enough to be clear, not overwhelming with insignificant details

LABOR SPECIFICITY:
- State WHO is doing the work (technician, crew, specialist)
- State WHAT work is being performed
- State WHEN/timeline for the work
- State WHERE (specific location/site)
- State WHY (purpose, benefit, code requirement)
- Include requisite procedures: "pump out and dispose of contaminated fuel lines"
- Specify scope boundaries: "excludes electrical permits" or "does not include crane rental"

INSTRUCTIONS:
Write a comprehensive, professional scope of work document following this structure:

1. PROJECT OVERVIEW
   - Brief introduction addressing ${context.customerName}
   - High-level summary of what work will be performed
   - Why this work matters (reliability, compliance, preventing failures)
   - Timeline overview (be realistic based on line items)

2. DETAILED SCOPE OF WORK
   - Organize work logically (by phase, system, or location)
   - For MATERIALS: Include quantities, specify key components, mention proprietary items
   - For LABOR: Who, what, when, where, why + requisite procedures
   - For each work category:
     * What will be done and how (methodology)
     * Relevant standards (NFPA, NEC, manufacturer specs)
     * Specific equipment, materials, and techniques
     * Any complications or special procedures required

3. DELIVERABLES & OUTCOMES
   - Specific deliverables (equipment installed, systems tested, documentation provided)
   - Expected performance outcomes
   - Quality standards and inspection procedures
   - Any warranties or guarantees

4. SITE WORK & COORDINATION
   - Site preparation requirements
   - Customer responsibilities (access, utilities, staging area)
   - Safety and compliance considerations
   - Work schedule and coordination needs

5. EXCLUSIONS (IMPORTANT - Be explicit)
   - Clearly state what is NOT included in this proposal
   - Items that may require separate quotes (permits, crane rental, etc.)
   - Customer-provided items or responsibilities

TONE & STYLE:
- Professional but approachable
- Clear and easy to understand (avoid excessive jargon)
- Confident and authoritative
- Client-focused (explain benefits, not just tasks)
- Use active voice

FORMAT:
- Use clear section headers
- Use bullet points for lists
- Keep paragraphs concise (3-4 sentences max)
- No HTML formatting - use plain text with line breaks

QUALITY STANDARDS:
- Be specific about inspection, testing, and commissioning procedures
- Reference relevant industry standards (NFPA, NEC, manufacturer specs)
- Include quality assurance checkpoints`;

    const { response } = await complete(
      {
        messages: [
          {
            role: 'system',
            content: `You are a proposal specialist for a generator service and maintenance company with 15+ years of experience. You write scopes of work that follow the company's proven methodology for increasing approval rates.

COMPANY PHILOSOPHY:
"Make it specific, easy to understand, and easy to say yes to."

Every proposal must give the customer:
- Clear understanding of WHAT they're getting
- Confidence in WHY it matters
- Trust in HOW it will be done
- Clarity on WHO is doing the work
- Understanding of WHEN it will happen

TECHNICAL EXPERTISE:
- Generator systems: Transfer switches, controllers, fuel systems, exhaust, cooling
- Service work: PM schedules, load bank testing, inspections, diagnostics
- Installation: Site prep, electrical connections, commissioning, code compliance
- Standards: NFPA 110, NEC Article 700, manufacturer specifications
- Project coordination: Customer coordination, safety, timelines

WRITING STANDARDS (from company quoting guide):

MATERIALS DESCRIPTIONS:
✓ Specific with quantities: "Four (4) 5-gallon fuel filters with mounting brackets"
✓ Group details strategically: "includes necessary fittings, gaskets, and adapters"
✓ Maintain information advantage: Don't itemize every nut and bolt
✗ Avoid vague terms: Never "Misc. Materials" - always specify what's covered

LABOR DESCRIPTIONS:
✓ Who: "Certified technician" or "two-person installation crew"
✓ What: Specific tasks and procedures
✓ When: Timeline and schedule details
✓ Where: Specific site/location details
✓ Why: Purpose, benefit, or code requirement
✓ Procedures: "pump out and properly dispose of contaminated fuel lines"
✓ Exclusions: "excludes electrical permits and inspections"

TONE:
- Professional yet conversational (not corporate/stiff)
- Detail-oriented without overwhelming
- Customer-focused: explain benefits, not just tasks
- Confidence-building: emphasize expertise and quality
- Clear scope boundaries to prevent misunderstandings

Your mission: Write scopes that customers understand, trust, and approve.`,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        maxTokens: 2000,
        temperature: 0.7,
        model: GEMINI_MODELS.LITE, // Use full Flash for complex scope of work generation
      },
      'scope_of_work_generator',
      'scope_of_work'
    );

    return response.content;
  };

  return {
    generateScopeOfWork,
    isEnabled: isEnabled && !budgetExceeded,
  };
}

/**
 * Hook for email subject line optimization
 */
export function useEmailOptimizer() {
  const { complete, isEnabled, budgetExceeded } = useAI();

  const optimizeEmailSubject = async (context: {
    quoteNumber: string;
    customerName: string;
    projectType?: string;
    currentSubject?: string;
    siteName?: string;
    technicianName?: string;
    isFollowUp?: boolean;
  }): Promise<string[]> => {
    const prompt = `Generate 3 optimized email subject lines for a generator service quote/proposal email:

CONTEXT:
- Quote Number: ${context.quoteNumber}
- Customer: ${context.customerName}
- Site Name: ${context.siteName || context.customerName}
- Project Type: ${context.projectType || 'Generator Service'}
- Technician: ${context.technicianName || 'Our technician'}
- Email Type: ${context.isFollowUp ? 'Follow-up reminder' : 'Initial proposal'}
- Current Subject: ${context.currentSubject || `Quote ${context.quoteNumber}`}

QUOTING BEST PRACTICES FROM COMPANY GUIDE:
Your subject lines must be:
✓ Clear, professional, and action-oriented
✓ Personalized with specific site or generator details when possible
✓ Creating urgency without being pushy
✓ 100% free of typos, misspellings, or mis-capitalizations
✗ Avoid spammy words: FREE, ACT NOW, URGENT (all caps), !!!

PROVEN EXAMPLES FROM OUR GUIDE:
- "[Customer Name], Recommended Generator Repairs from Recent Inspection"
- "Action Needed: Important Generator Service Recommendations"
- "Follow-Up: Critical Generator Maintenance Proposal Inside"
- "[Site Name] Generator Inspection Findings - Next Steps"

STRATEGY - Generate 3 variants:
1. INSPECTION-BASED: Reference recent inspection, mention site/generator
   Focus on findings and recommendations from technician

2. ACTION-ORIENTED: Clear next steps, professional urgency
   Emphasize importance without being pushy

3. VALUE/BENEFIT: Highlight reliability, prevention, or system readiness
   Customer-focused benefits (avoid downtime, ensure reliability)

REQUIREMENTS:
- Use ${context.siteName || context.customerName} for personalization
- Mention "Generator" explicitly for clarity
- Keep under 65 characters when possible
- Professional capitalization (title case or sentence case only)
- Action-oriented but respectful tone
- NO spam trigger words (FREE, URGENT in caps, !!!, ACT NOW)

FORMATTING:
- Return ONLY the 3 subject lines
- One subject line per line
- No numbering, bullets, or explanations
- Perfect spelling and capitalization

Generate the 3 optimized subject lines now:`;

    const { response } = await complete(
      {
        messages: [
          {
            role: 'system',
            content: `You are an email communication specialist for a generator service and maintenance company. You craft subject lines following proven best practices that increase proposal approval rates.

COMPANY QUOTING PHILOSOPHY:
Our proposals must be "specific, easy to understand, and easy to say yes to." Every subject line should make the recipient want to open the email and understand what's inside.

EMAIL PSYCHOLOGY & PROVEN TACTICS:
- Subject lines with 50-65 characters have highest open rates
- Personalization (site name, customer name) increases opens by 26%
- Generator service customers value: reliability, preventing downtime, peace of mind
- Reference recent inspections/technician findings builds credibility
- Action-oriented without being pushy increases engagement
- 100% perfect spelling and capitalization builds trust

GENERATOR SERVICE INDUSTRY CONTEXT:
- Customers: Facilities managers, business owners, homeowners with backup power needs
- Decision drivers: Preventing unexpected failures, ensuring code compliance, avoiding downtime
- Trust factors: Technician expertise, thoroughness of inspection, clarity of recommendations
- Timeline sensitivity: "Why now?" matters - tie to recent inspection or upcoming need

COMPANY VOICE & STANDARDS:
- Professional but approachable (not corporate/stiff)
- Specific and clear (avoid vague terms like "important" without context)
- Respectful urgency (emphasize importance without pressure tactics)
- Zero tolerance for: spam words (FREE, ACT NOW, URGENT in caps), typos, mis-capitalizations, excessive punctuation (!!!)

PROVEN PATTERNS FROM COMPANY GUIDE:
✓ "[Customer/Site], Recommended Generator Repairs from Recent Inspection"
✓ "Action Needed: Important Generator Service Recommendations"
✓ "[Site Name] Generator Inspection Findings - Next Steps"
✓ "Follow-Up: Critical Generator Maintenance Proposal Inside"

Your mission: Write subject lines that busy decision-makers will open because they're clear, relevant, and trustworthy.`,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        maxTokens: 250,
        temperature: 0.8,
        model: GEMINI_MODELS.LITE, // Use Flash Lite for simple subject line generation (87% cost savings)
      },
      'email_subject_optimizer',
      'email_subject'
    );

    // Parse response into array
    const subjects = response.content
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && !s.startsWith('#') && !s.match(/^(Example|Variant|Option|\d+\.|\d+\))/i))
      .slice(0, 3);

    return subjects;
  };

  return {
    optimizeEmailSubject,
    isEnabled: isEnabled && !budgetExceeded,
  };
}

/**
 * Hook for generating proposal email bodies
 */
export function useProposalEmailGenerator() {
  const { complete, isEnabled, budgetExceeded } = useAI();

  const generateEmailBody = async (context: {
    customerName: string;
    siteName?: string;
    technicianName: string;
    quoteNumber: string;
    projectType?: string;
    totalCost?: number;
    proposedTimeline?: string;
    keyFindings?: string[];
    isFollowUp?: boolean;
    followUpWeek?: number; // 1, 3, or 6 for follow-up timing
  }): Promise<string> => {
    const followUpType = context.isFollowUp
      ? context.followUpWeek === 1
        ? 'First Follow-Up (Week 1)'
        : context.followUpWeek === 3
        ? 'Second Follow-Up (Week 3)'
        : 'Final Follow-Up (Week 6)'
      : 'Initial Proposal';

    const prompt = `Generate a professional email body for a generator service proposal/quote:

CONTEXT:
- Email Type: ${followUpType}
- Customer: ${context.customerName}
- Site: ${context.siteName || context.customerName}
- Technician: ${context.technicianName}
- Quote Number: ${context.quoteNumber}
- Project: ${context.projectType || 'Generator Service'}
- Total Cost: ${context.totalCost ? `$${context.totalCost.toLocaleString()}` : 'See attached proposal'}
- Proposed Timeline: ${context.proposedTimeline || 'Upon approval'}
- Key Findings: ${context.keyFindings && context.keyFindings.length > 0 ? context.keyFindings.join('; ') : 'See attached inspection report'}

COMPANY EMAIL STRUCTURE (from quoting guide):

${!context.isFollowUp ? `
1. OPENING PARAGRAPH:
   - Acknowledge the recent inspection
   - Reference technician by name (appeal to expertise and authority)
   - Reference attached inspection report
   - Demonstrate empathy and candor

   Example: "Following our recent inspection at ${context.siteName || context.customerName}, our technician, ${context.technicianName}, has identified some critical areas requiring attention to ensure the continued reliability of your generator system. Please find the attached report outlining these findings and our recommended next steps."

2. KEY PROPOSAL ELEMENTS (Answer these questions):
   - Why is it important to do this work now?
   - What might happen if we don't do this work?
   - Why is what we're proposing important?
   - How much will it cost?
   - When can we do the work?
   - What can they expect from us if they approve?

   Example: "Our proposal outlines the necessary repairs to prevent potential failures, ensuring your generator is ready when you need it most. We recommend proceeding with these updates as soon as possible to avoid unexpected downtime and costly emergency repairs."

3. CALL TO ACTION:
   - Ask for confirmation of receipt
   - Offer to answer questions
   - Provide clear next step

   Example: "Please confirm receipt of this proposal and let us know if you have any questions. We are available to discuss further at your convenience."
` : context.followUpWeek === 1 ? `
WEEK 1 FOLLOW-UP:
   - Friendly check-in
   - Reiterate importance
   - Ask if they have questions

   Example: "Just checking in to see if you had a chance to review our proposal for your generator at ${context.siteName || context.customerName}. Please let us know if you have any questions or need further information."
` : context.followUpWeek === 3 ? `
WEEK 3 FOLLOW-UP:
   - Check on decision timeline
   - Offer additional details or alternatives
   - Ask if they think they want to proceed

   Example: "Following up on our proposal from [date]. We understand you may need time to review and budget for this work. Are there any additional details we can provide, or would an alternative approach better suit your needs?"
` : `
WEEK 6 FINAL FOLLOW-UP:
   - Graceful exit
   - Leave door open
   - Offer to incorporate into next maintenance
   - Assume we're not getting the work (for now)

   Example: "This will be our final follow-up on the proposed generator repairs. We understand timing and budget considerations can delay projects. If you'd like, we can incorporate these recommendations into your next scheduled maintenance visit. Please don't hesitate to reach out when you're ready to proceed."
`}

TONE REQUIREMENTS:
- Professional but approachable (not corporate/stiff)
- Build trust through clarity and expertise
- Demonstrate empathy and understanding
- Clear about trade-offs and consequences
- Confident without being pushy

FORMATTING:
- Use proper greeting: "Dear ${context.customerName},"
- Proper paragraphs with line breaks
- Professional closing: "Best regards," or "Sincerely,"
- No HTML - plain text format
- Include: "Please confirm receipt of this proposal."

Generate the complete email body now:`;

    const { response } = await complete(
      {
        messages: [
          {
            role: 'system',
            content: `You are an email communication specialist for a generator service company. You write proposal emails that follow proven best practices from the company's quoting guide.

COMPANY PHILOSOPHY:
"Make it specific, easy to understand, and easy to say yes to."

Your emails must:
- Build trust through technician expertise and clear communication
- Demonstrate empathy and understanding of customer constraints
- Explain trade-offs candidly (what happens if work isn't done)
- Answer key questions: Why now? What if we don't? How much? When? What to expect?
- Create clear paths to acceptance (phone, email, proposal link)

EMAIL BEST PRACTICES:
- Acknowledge the inspection and reference the technician by name
- Appeal to the technician's authority and expertise
- Be specific about findings and recommendations
- Explain consequences of inaction (reliability, compliance, cost)
- Provide clear next steps and multiple ways to respond
- Professional but conversational tone

FOLLOW-UP STRATEGY:
- Week 1: Friendly check-in, reiterate importance, offer to answer questions
- Week 3: Check decision timeline, offer alternatives, ask about likelihood
- Week 6: Graceful exit, leave door open, offer to incorporate later

Your mission: Write emails that customers read, understand, and respond to positively.`,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        maxTokens: 800,
        temperature: 0.7,
        model: GEMINI_MODELS.LITE, // Use Flash for email body generation (higher quality needed)
      },
      'proposal_email_generator',
      'email_body'
    );

    return response.content;
  };

  return {
    generateEmailBody,
    isEnabled: isEnabled && !budgetExceeded,
  };
}

/**
 * Hook for optimizing line item descriptions
 * Enhances materials and labor descriptions with specificity from company guide
 */
export function useLineItemOptimizer() {
  const { complete, isEnabled, budgetExceeded } = useAI();

  const optimizeDescription = async (context: {
    currentDescription: string;
    itemType: 'material' | 'labor' | 'equipment' | 'service';
    category?: string;
    quantity?: number;
    unit?: string;
    relatedItems?: string[]; // Other line items in the quote for context
  }): Promise<string[]> => {
    if (!isEnabled || budgetExceeded) {
      throw new Error('AI features are not available');
    }

    const isMaterial = context.itemType === 'material' || context.itemType === 'equipment';
    const isLabor = context.itemType === 'labor' || context.itemType === 'service';

    const prompt = `Optimize this line item description for a generator service quote following company best practices:

CURRENT DESCRIPTION:
"${context.currentDescription}"

ITEM DETAILS:
- Type: ${context.itemType}
- Category: ${context.category || 'Not specified'}
${context.quantity ? `- Quantity: ${context.quantity} ${context.unit || ''}` : ''}
${context.relatedItems && context.relatedItems.length > 0 ? `- Related Items in Quote: ${context.relatedItems.join('; ')}` : ''}

COMPANY QUOTING GUIDE - LINE ITEM BEST PRACTICES:

${isMaterial ? `
MATERIALS DESCRIPTION STANDARDS:
1. BE SPECIFIC WITH QUANTITIES AND DETAILS
   ✓ Good: "Four (4) 5-gallon fuel filters with mounting brackets"
   ✗ Bad: "Fuel filters"

2. GROUP SHOPPABLE ITEMS STRATEGICALLY
   ✓ Good: "Heavy-duty fuel pump assembly (includes necessary fittings, gaskets, and adapters)"
   ✗ Bad: List every small fitting individually

3. INCLUDE PROPRIETARY/VALUE-ADD DETAILS
   ✓ Good: "OEM-certified control board with pre-configured settings"
   ✓ Good: "Industrial-grade battery cables with corrosion-resistant terminals"

4. AVOID VAGUE LUMP SUMS
   ✗ Never: "Misc. Materials"
   ✓ Instead: "Miscellaneous installation hardware (mounting bolts, wire ties, labels)"

5. SPECIFY GRADES/STANDARDS WHEN RELEVANT
   ✓ "Schedule 40 PVC conduit"
   ✓ "10W-30 synthetic oil (API CK-4 rated)"
   ✓ "UL-listed transfer switch"
` : ''}

${isLabor ? `
LABOR DESCRIPTION STANDARDS:
1. STATE WHO IS DOING THE WORK
   ✓ "Certified technician will perform..."
   ✓ "Two-person installation crew..."
   ✓ "Factory-trained service technician..."

2. STATE WHAT WORK IS BEING PERFORMED
   ✓ Be specific about procedures and tasks
   ✓ Include key steps: "diagnose, repair, test, and document"

3. STATE WHEN/TIMELINE
   ✓ "Scheduled during normal business hours"
   ✓ "Emergency response within 4 hours"
   ✓ "Two-day installation process"

4. STATE WHERE (SPECIFIC LOCATION/SITE)
   ✓ "On-site at customer facility"
   ✓ "At main generator location, Building A"

5. STATE WHY (PURPOSE, BENEFIT, CODE REQUIREMENT)
   ✓ "...to ensure compliance with NFPA 110"
   ✓ "...to restore reliable backup power"
   ✓ "...required for annual inspection certification"

6. INCLUDE REQUISITE PROCEDURES
   ✓ Good: "Pump out and properly dispose of contaminated fuel lines per EPA guidelines"
   ✓ Good: "Load bank testing to verify 100% rated capacity"

7. SPECIFY SCOPE BOUNDARIES/EXCLUSIONS
   ✓ "Excludes electrical permits and utility coordination"
   ✓ "Does not include concrete pad modifications"
   ✓ "Customer to provide temporary power during work"
` : ''}

OVERALL REQUIREMENTS:
- Make it specific, easy to understand, and easy to say yes to
- Customer should know EXACTLY what they're getting
- Demonstrate expertise and value without overwhelming with jargon
- Professional tone, complete sentences preferred for complex items
- Perfect spelling and capitalization

INSTRUCTIONS:
Generate 3 optimized versions of this line item description:
1. CONCISE VERSION: Streamlined but still specific (good for simple/standard items)
2. DETAILED VERSION: Comprehensive with full context (good for complex/expensive items)
3. VALUE-FOCUSED VERSION: Emphasizes benefits and quality (good for competitive situations)

Return ONLY the 3 descriptions as a JSON array, no additional text.`;

    const { response } = await complete(
      {
        messages: [
          {
            role: 'system',
            content: `You are a proposal specialist for a generator service and maintenance company with expertise in writing clear, professional line item descriptions.

COMPANY PHILOSOPHY:
"Make it specific, easy to understand, and easy to say yes to."

Your descriptions must balance:
- Technical accuracy and specificity
- Clear value communication
- Professional presentation
- Customer comprehension

MATERIALS EXPERTISE:
- Include quantities, grades, standards when relevant
- Group small items strategically ("includes necessary fittings, gaskets, and adapters")
- Never use vague terms like "Misc. Materials"
- Emphasize OEM parts, certifications, quality grades when applicable

LABOR EXPERTISE:
- Always clarify who, what, when, where, why
- Include requisite procedures: "pump out and dispose of...", "test and document..."
- State boundaries: "excludes permits", "customer to provide..."
- Connect to outcomes: "to ensure compliance", "to restore reliable operation"

INDUSTRY CONTEXT:
You work with backup generators, transfer switches, fuel systems, control systems, and related equipment. Common materials include filters, batteries, fluids, control boards, switches. Common labor includes preventive maintenance, load testing, diagnostics, repairs, installations.`,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        maxTokens: 600,
        temperature: 0.7,
        model: GEMINI_MODELS.LITE, // Simple completion task, use lite version
      },
      'line_item_optimizer',
      'line_item_description'
    );

    // Parse the JSON array response
    if (!response.content?.trim()) {
      return [];
    }
    try {
      if (!response.content?.trim()) return [];
      const suggestions = JSON.parse(response.content);
      if (Array.isArray(suggestions) && suggestions.length === 3) {
        return suggestions;
      }
      throw new Error('Invalid response format');
    } catch (parseError) {
      // Fallback: try to extract descriptions from text response
      if (!response.content) return [];
      const lines = response.content.split('\n').filter((line) => line.trim().length > 20);
      return lines.slice(0, 3);
    }
  };

  return {
    optimizeDescription,
    isEnabled: isEnabled && !budgetExceeded,
  };
}

/**
 * Hook for smart autocomplete suggestions
 */
export function useSmartAutocomplete() {
  const { complete, isEnabled, budgetExceeded } = useAI();

  const getSuggestions = async (context: {
    fieldName: string;
    partialText: string;
    category?: string;
    recentValues?: string[];
  }): Promise<string[]> => {
    // Determine field context for better suggestions
    const fieldLower = context.fieldName.toLowerCase();
    const isDescriptionField = fieldLower.includes('description') || fieldLower.includes('item');
    const isNoteField = fieldLower.includes('note') || fieldLower.includes('comment');
    const isCategoryField = fieldLower.includes('category') || fieldLower.includes('type');

    const prompt = `Provide intelligent autocomplete suggestions for a generator service quote form field:

FIELD INFORMATION:
- Field Name: ${context.fieldName}
- Current Partial Text: "${context.partialText}"
- Category/Context: ${context.category || 'General'}
- Recently Used Values: ${context.recentValues && context.recentValues.length > 0 ? context.recentValues.join(', ') : 'None available'}
- Field Type: ${isDescriptionField ? 'Item Description' : isCategoryField ? 'Category/Type' : isNoteField ? 'Notes/Comments' : 'General Text'}

CONTEXT ABOUT GENERATOR SERVICE INDUSTRY:
This is for a quoting system used by generator service and installation companies. Common items include:
- Equipment: Generators (diesel, natural gas, propane, bi-fuel), transfer switches, controllers, batteries
- Services: Preventive maintenance, load bank testing, inspections, repairs, troubleshooting
- Installation: New generator installation, transfer switch installation, fuel system installation
- Parts: Filters (oil, air, fuel), batteries, belts, hoses, fluids, control boards
- Labor: Diagnostic time, technician labor, project management

INSTRUCTIONS:
1. Complete the partial text naturally and professionally
2. Suggest 3-5 completions that are:
   - Specific and detailed (avoid generic terms)
   - Technically accurate for generator service work
   - Professional and quote-ready
   - Properly formatted with correct capitalization
3. Consider recent values to maintain consistency
4. If partial text is very short (1-3 chars), suggest common items in that category
5. If partial text is longer, complete it logically

RESPONSE FORMAT:
- Return ONLY the completed text suggestions
- One suggestion per line
- No numbering, bullets, or explanations
- Each suggestion should be a complete, usable value
- Order by relevance (most likely completion first)

Example for partial "Annual" in description field:
Annual Preventive Maintenance Service - Diesel Generator
Annual Service Contract - 4 Visits Per Year
Annual Load Bank Test and Inspection
Annual Maintenance Package with Parts

Generate suggestions now:`;

    const { response } = await complete(
      {
        messages: [
          {
            role: 'system',
            content: `You are an expert in generator service and installation work with deep knowledge of:

GENERATOR SYSTEMS:
- Equipment types: Standby generators (diesel, natural gas, propane, bi-fuel)
- Sizes: Residential (7-48kW), Commercial (50-2000kW), Industrial (2MW+)
- Brands: Generac, Kohler, Cummins, Caterpillar, MTU, Detroit Diesel
- Components: Transfer switches (ATS), controllers, fuel systems, exhaust systems, cooling systems

SERVICE WORK:
- Preventive Maintenance: Oil changes, filter replacements, battery service, coolant service
- Testing: Load bank testing, transfer switch testing, automatic start testing
- Inspections: Visual inspections, fluid level checks, leak detection, electrical connections
- Repairs: Engine repairs, electrical troubleshooting, fuel system repairs, control panel repairs

INSTALLATION WORK:
- Site preparation: Concrete pads, conduit runs, fuel tank installation
- Equipment installation: Generator set, transfer switch, control panels
- Electrical connections: Utility service, load connections, control wiring
- Commissioning: System startup, load testing, customer training

PARTS & MATERIALS:
- Routine parts: Oil filters, air filters, fuel filters, spark plugs, batteries
- Fluids: Engine oil, coolant, fuel additives
- Electrical: Fuses, relays, contactors, control boards
- Fuel system: Fuel lines, fuel pumps, fuel filters, day tanks

Your suggestions should:
- Be specific and actionable
- Use industry-standard terminology
- Reflect real-world service offerings
- Be professional and quote-ready
- Account for common variations in phrasing`,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        maxTokens: 200,
        temperature: 0.6,
        model: GEMINI_MODELS.LITE, // Use Flash Lite for autocomplete (87% cost savings)
      },
      'smart_autocomplete',
      'autocomplete'
    );

    const suggestions = response.content
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && !s.match(/^(Example|Suggestion|\d+\.|\d+\)|-):/i))
      .slice(0, 5);

    return suggestions;
  };

  return {
    getSuggestions,
    isEnabled: isEnabled && !budgetExceeded,
  };
}
